import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from './button';
import { EmptyState } from './display';
import { Skeleton } from './spinner';

export interface SortState {
  field: string;
  desc: boolean;
}

/** `sort` string for the API from a SortState ("-createdAt"). */
export const sortParam = (s: SortState | null) => (s ? `${s.desc ? '-' : ''}${s.field}` : undefined);

interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[] | undefined;
  loading?: boolean;
  /** Server-side sorting: columns with `meta.sortField` become clickable. */
  sort?: SortState | null;
  onSortChange?: (s: SortState) => void;
  onRowClick?: (row: T) => void;
  rowHref?: (row: T) => string;
  empty?: ReactNode;
  /** The list query's error: shown in place of the rows, so a failed load is not an empty table. */
  error?: unknown;
  /** Pagination footer (server-side). */
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  className?: string;
  dense?: boolean;
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    sortField?: string;
    className?: string;
    headerClassName?: string;
  }
}

/** The one table: server-side sort and paging, row links, loading skeleton, empty state. */
export function DataTable<T>({ columns, data, loading, sort, onSortChange, onRowClick, rowHref, empty, error, page, pageSize, total, onPageChange, className, dense }: DataTableProps<T>) {
  const navigate = useNavigate();
  const table = useReactTable({ data: data ?? [], columns, getCoreRowModel: getCoreRowModel(), manualSorting: true, manualPagination: true });
  const rows = table.getRowModel().rows;
  const pageCount = total !== undefined && pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const cell = dense ? 'px-3 py-1.5' : 'px-3 py-2.5';

  return (
    <div className={cn('overflow-hidden rounded-lg border bg-surface shadow-card', className)}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-surface-2">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b">
                {hg.headers.map((h) => {
                  const field = h.column.columnDef.meta?.sortField;
                  const active = field && sort?.field === field;
                  return (
                    <th
                      key={h.id}
                      scope="col"
                      aria-sort={active ? (sort?.desc ? 'descending' : 'ascending') : undefined}
                      className={cn('whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted', h.column.columnDef.meta?.headerClassName)}
                    >
                      {field && onSortChange ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 hover:text-fg"
                          onClick={() => onSortChange({ field, desc: active ? !sort?.desc : false })}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {active ? sort?.desc ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" /> : null}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && !data
              ? Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b last:border-0">
                    {columns.map((_c, j) => (
                      <td key={j} className={cell}>
                        <Skeleton className="h-4 w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row) => {
                  const href = rowHref?.(row.original);
                  const clickable = Boolean(onRowClick || href);
                  return (
                    <tr
                      key={row.id}
                      className={cn('border-b last:border-0', clickable && 'cursor-pointer hover:bg-surface-hover')}
                      onClick={(e) => {
                        // A click inside a portal (a row's action menu, a dialog) bubbles here through React but isn't on the row.
                        if (!e.currentTarget.contains(e.target as Node) || (e.target as HTMLElement).closest('a,button,input,select,label')) return;
                        if (onRowClick) onRowClick(row.original);
                        else if (href) {
                          if (e.metaKey || e.ctrlKey) window.open(href, '_blank');
                          else navigate(href);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((c) => (
                        <td key={c.id} className={cn(cell, 'align-middle', c.column.columnDef.meta?.className)}>
                          {flexRender(c.column.columnDef.cell, c.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      {error && !data ? <EmptyState title="Couldn't load this list" description={errorMessage(error)} /> : null}
      {!loading && data && data.length === 0 ? (empty ?? <EmptyState title="Nothing here yet" />) : null}
      {onPageChange && total !== undefined && total > (pageSize ?? 0) ? (
        <div className="flex items-center justify-between border-t bg-surface-2 px-3 py-2 text-xs text-muted">
          <span>
            {((page ?? 1) - 1) * (pageSize ?? 0) + 1}–{Math.min((page ?? 1) * (pageSize ?? 0), total)} of {total}
          </span>
          <div className="flex items-center gap-1">
            <Button size="icon-sm" variant="ghost" aria-label="Previous page" disabled={(page ?? 1) <= 1} onClick={() => onPageChange((page ?? 1) - 1)}>
              <ChevronLeft />
            </Button>
            <span className="px-1 tabular-nums">
              {page} / {pageCount}
            </span>
            <Button size="icon-sm" variant="ghost" aria-label="Next page" disabled={(page ?? 1) >= pageCount} onClick={() => onPageChange((page ?? 1) + 1)}>
              <ChevronRight />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
